import { push } from "connected-react-router"
import { call, put, take, takeEvery } from "redux-saga/effects"
import { v4 as uuidv4 } from 'uuid';
import config from '../../config.json'
import actionTypes from "../../actions/actionTypes"
import { getApiTokenCall, sessionDeleteCall, sessionGetCall, userLogoutCall } from "../../apis/wpApi/apiCalls"
import authUser from "../../apis/wpApi/authUser"
import { isLoginPage, putError } from "../helpers"
import { putCapsData, putSessionData, validSessionResponse } from "./sessionHelpers";


function* getSession(token, forceGetSession, bypassSession) {
    let session = yield call(authUser.getSessionToken)
    let data = false

    if(!session) {
        session = yield uuidv4()
        yield call(authUser.setSessionToken, session)
    }

    try {
        const response = yield call(sessionGetCall, token, {
            forceSession: forceGetSession,
            bypassSession: bypassSession
        })

        const valid = yield call(validSessionResponse, response)

        if(!valid) {
            throw new Error('Malformed get session response')
        }

        data = yield response.data
    } catch(e) {
        if(!e.hasOwnProperty('response')) {
            throw new Error('Get session error')
        }

        const valid = yield call(validSessionResponse, e.response)

        if(!valid || 403 !== e.response.status) {
            throw new Error('Malformed get session response')
        }

        data = yield e.response.data
    } finally {
        return yield data
    }
}


function* getToken(username, password) {
    const response = yield call(getApiTokenCall, username, password)

    if(
        200 === response.status && 
        response.hasOwnProperty('data') && 
        response.data.hasOwnProperty('token') &&
        response.data.token
    ) {
        yield call(authUser.setToken, response.data.token)

        return yield response.data.token
    } else {
        throw new Error('No token or caps')
    }
}



function* doLogout(clientOnly = true) {
    const isLogin = yield call(isLoginPage)
    const token = yield call(authUser.getToken)

    try { 
        if(clientOnly) {
            yield call(sessionDeleteCall, token)
        } else {
            yield call(userLogoutCall, token) 
        }
    } 

    catch(e) {
        if(
            !e ||
            !e.hasOwnProperty('response') ||
            !e.response ||
            !e.response.hasOwnProperty('status') ||
            401 !== e.response.status
        ) {
            console.log('Logout error', e)
        }
    }
    
    finally {
        yield call(authUser.clearToken)
        yield call(authUser.clearSessionToken)

        if(!isLogin) {
            yield put(push(config.routes.logout.path))
        }
    
        yield put({
            type: actionTypes.CLEAR_ALL_DATA
        })
    }
}


function* doLogin(sessionData) {
    yield put({
        type: actionTypes.CLEAR_ALL_DATA
    })

    if(!sessionData.hasOwnProperty('caps')) {
        throw new Error('Invalid caps')
    }

    yield call(putCapsData, sessionData.caps)

    yield put(push('/'))
}


function* tryLogin(sessionData) {
    if(
        3 === sessionData.state ||
        2 === sessionData.state
    ) {
        yield call(putSessionData, sessionData)
        yield call(putError, actionTypes.AUTH_FLOW_ERROR, 'Client is in use')
    } else if(1 === sessionData.state  && sessionData.expired && sessionData.caps) {
        yield call(doLogin, sessionData)
        yield call(putSessionData, sessionData)

        yield put({
            type: actionTypes.AUTH_FLOW_SUCCESS
        })
        yield put({
            type: actionTypes.LOAD_ALL_DATA_REQUEST
        })
    } else {
        throw new Error('Invalid session data')
    }
}


function* login(forceSession, bypassSession, action) {
    let token = yield false

    if(forceSession) {
        token = yield call(authUser.getToken)
    } else {
        token = yield call(getToken, action.payload.username, action.payload.password)
    }

    const sessionData = yield call(getSession, token, forceSession, bypassSession)

    yield call(tryLogin, sessionData)
}


function* logout(clientOnly) {
    try {
        yield call(doLogout, clientOnly)
        yield put({
            type: actionTypes.AUTH_FLOW_SUCCESS,
        })
    } catch(e) {
        yield call(putError, actionTypes.AUTH_FLOW_ERROR, e.message)
    }
}


function* auth(action) {
    try {
        if(actionTypes.AUTH_FLOW_LOGIN_REQUEST_SESSION === action.type) {
            yield call(login, false, false, action)
        } else if(actionTypes.AUTH_FLOW_LOGIN_REQUEST_FORCE_SESSION === action.type) {
            yield call(login, true, false, action)
        } else if(actionTypes.AUTH_FLOW_LOGIN_REQUEST_BYPASS === action.type) {
            yield call(login, false, true, action)
        } else if(actionTypes.AUTH_FLOW_LOGIN_REQUEST_FORCE_BYPASS === action.type)  {
            yield call(login, true, true, action)
        } else if(actionTypes.AUTH_FLOW_LOGOUT_REQUEST_WP === action.type) {
            yield call(logout, false)
        } else {
            yield call(logout, true)
        }
    } catch(e) {
        yield call(doLogout, true)
        yield call(putError, actionTypes.AUTH_FLOW_ERROR, e.message)
    }
}


function* authFlow() {
    yield takeEvery([
        actionTypes.AUTH_FLOW_LOGIN_REQUEST_SESSION, 
        actionTypes.AUTH_FLOW_LOGIN_REQUEST_FORCE_SESSION, 
        actionTypes.AUTH_FLOW_LOGIN_REQUEST_BYPASS,
        actionTypes.AUTH_FLOW_LOGIN_REQUEST_FORCE_BYPASS,
        actionTypes.AUTH_FLOW_LOGOUT_REQUEST_CLIENT,
        actionTypes.AUTH_FLOW_LOGOUT_REQUEST_WP
    ], auth)
}

export default authFlow